'use client'

import type { Hotspot, HotspotAction } from '@/types/interactiveVideo'
import { ArrowLeft, ArrowRight, ArrowUpRight, ChevronsLeft, Play, X } from 'lucide-react'
import { FC, useState } from 'react'
import { formatPlaybackTime } from './formatPlaybackTime'
import { WIDE_FONT_FAMILY } from './wideFont'

/** 目次の一覧の幅（狭い画面でボタンから開くとき） */
export const WIDE_AGENDA_PANEL_WIDTH = 268

/**
 * PC で動画の左に重ねるときの幅。動画の幅の24%にし、320〜340px の間で動かす。
 * 動画が大きいとき（1440px）は340px、それより小さい動画（ノートPC・管理画面のプレビュー）では320pxほど。
 * 下限を268pxにしていたときは、ふつうの長さの項目名（14文字ほど）でも2行に折り返していた。
 * 22%・300〜320px にしていたときは、ふつうのPC（動画の幅1360pxほど）で下限の300pxに張り付いていた
 */
const WIDE_AGENDA_SIDE_RATIO = 0.24
const WIDE_AGENDA_SIDE_MIN_WIDTH = 320
const WIDE_AGENDA_SIDE_MAX_WIDTH = 340
export const WIDE_AGENDA_SIDE_WIDTH_CSS = `clamp(${WIDE_AGENDA_SIDE_MIN_WIDTH}px, ${WIDE_AGENDA_SIDE_RATIO * 100}%, ${WIDE_AGENDA_SIDE_MAX_WIDTH}px)`

/** 上の幅を数値で出す（管理画面の確認で、公開画面と同じ大きさの目次を縮めて見せるため） */
export const wideAgendaSideWidth = (frameWidth: number): number =>
  Math.min(Math.max(frameWidth * WIDE_AGENDA_SIDE_RATIO, WIDE_AGENDA_SIDE_MIN_WIDTH), WIDE_AGENDA_SIDE_MAX_WIDTH)

/**
 * 動画に重ねる目次の背景（公開画面と管理画面の確認で共通）。グラデーションにせず、同じ濃さの単色にする。
 * 目次はお客様の動画の上に出るので、どの動画・テーマ色にもなじむ黒の半透明にする（特定の色味を持たせない）
 */
export const WIDE_AGENDA_OVERLAY_BACKGROUND = 'rgba(0, 0, 0, 0.72)'

const PANEL_BG = '#141414'
// 選んでいる行・マウスを乗せた行・時間の文字も、背景に合わせて色味の無い白系にする
const CURRENT_BG = 'rgba(255,255,255,0.16)'
/** マウスを乗せた行。白く明るくすると映像が白っぽく抜けて見えるので、黒く沈める。今の場面の行は、乗せるとさらに少し明るくする */
const HOVER_BG = 'rgba(0,0,0,0.55)'
const CURRENT_HOVER_BG = 'rgba(255,255,255,0.22)'
const SUB_COLOR = '#d1d5db'

/** 目次の1行。目次をオンにした動画のインタラクティブボタン1つにあたる */
export type WideAgendaEntry = {
  id: string
  label: string
  action: HotspotAction
}

/**
 * 目次をオンにした動画で、目次の行にせず映像の上に出すボタンか。
 * 目次の行は動画の最初（0秒）から最後まで出す決まりで、目次の編集でも 0 秒で足す。
 * それより後から出るボタン（キャンバスで「動画の最後」を選んで足した最後の選択肢など）は、映像の上に出す。
 * 編集画面では、映像に出すボタンの出始めを WIDE_VIDEO_BUTTON_MIN_START 秒より前にできないようにして、
 * 目次の行と入れ替わらないようにしている
 */
export const isShownOnVideoWithAgenda = (hotspot: Pick<Hotspot, 'startTime'>): boolean => hotspot.startTime > 0

/** 目次をオンにした動画で、映像に出すボタンの出始めの下限（秒）。0 秒だと目次の行になってしまう */
export const WIDE_VIDEO_BUTTON_MIN_START = 1

/**
 * 動画のインタラクティブボタンを目次の行にする。映像の上に出すボタン（isShownOnVideoWithAgenda）は除く。並びは保存した順（目次の編集でドラッグして決めた順）のまま。
 * 以前は出始める時間の順に並べ直していたため、目次の編集で足した項目（出始めが0秒）がいつも先頭に来て、
 * 並べ替えもできなかった
 */
export const toAgendaEntries = (
  hotspots: readonly Pick<Hotspot, 'id' | 'label' | 'startTime' | 'action'>[],
): WideAgendaEntry[] =>
  hotspots
    .filter((hotspot) => !isShownOnVideoWithAgenda(hotspot))
    .map((hotspot) => ({ id: hotspot.id, label: hotspot.label || 'ボタン', action: hotspot.action }))

/** 移った直後の秒数のずれ（シーク位置の丸め）を「その場面に入った」とみなす幅 */
const SCENE_TOLERANCE_SECONDS = 0.3

/** 今見ている場面の行。同じ動画の秒数へ移る行のうち、再生位置が過ぎた最後のもの */
export const findCurrentAgendaEntryId = (entries: readonly WideAgendaEntry[], currentTime: number): string | null => {
  let current: { id: string; time: number } | null = null
  for (const entry of entries) {
    if (entry.action.type !== 'seek') continue
    const { time } = entry.action
    if (currentTime + SCENE_TOLERANCE_SECONDS >= time && (!current || time >= current.time)) {
      current = { id: entry.id, time }
    }
  }
  return current?.id ?? null
}

/** 「2 / 3」。今の場面が決まらない（最初の場面より前など）ときは件数だけ出す */
export const formatAgendaPosition = (entries: readonly WideAgendaEntry[], currentId: string | null): string => {
  const index = entries.findIndex((entry) => entry.id === currentId)
  return index >= 0 ? `${index + 1} / ${entries.length}` : `全${entries.length}件`
}

export type WideAgendaPanelProps = {
  entries: WideAgendaEntry[]
  /** 今見ている場面の行（findCurrentAgendaEntryId） */
  currentId: string | null
  /** 必須アンケートに答えている間など、押せなくする間 */
  disabled?: boolean
  /** 今の動画の CTA（あれば下部に出す） */
  cta?: { url: string; label: string } | null
  /**
   * 見出し右のボタン。'collapse' は PC で動画に重ねたとき（たたむ）、'close' はボタンから開いたとき（閉じる）。
   */
  headerAction?: { kind: 'collapse' | 'close'; onClick: () => void }
  /**
   * 2本目以降の動画で、目次のいちばん下に出す「← 前の動画へ」の行。目次を開いている間は、戻るボタンを
   * 動画の上に浮かせずここに置く（目次の帯からはみ出さないように）
   */
  backAction?: { onClick: () => void }
  /** 行を押した。インタラクティブボタンを押したときと同じ動作をする */
  onSelect: (id: string) => void
  style?: React.CSSProperties
}

/**
 * ワイド型の目次（ADR-0025）。目次をオンにした動画のインタラクティブボタンを並べ、
 * 押すとそのボタンと同じ動作（指定の秒数へ／別の動画へ／外部リンクへ）をする。
 * PC で動画に重ねる欄・スマホの下から出す欄・狭い画面でボタンから開く欄で同じ部品を使う。
 *
 * 再生が始まった時点から出し続ける。動画が終わったときに自動で開いたり、終了画面に
 * 「次の動画へ」を足したりはしない（ADR-0023 の「再生後の画面で別の動画へ進ませない」）。
 * 視聴数などの数字も出さない。
 */
const WideAgendaPanel: FC<WideAgendaPanelProps> = ({
  entries,
  currentId,
  disabled = false,
  cta,
  headerAction,
  backAction,
  onSelect,
  style,
}) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [isBackHovered, setIsBackHovered] = useState(false)
  const [isHeaderActionHovered, setIsHeaderActionHovered] = useState(false)
  return (
    <nav
      data-testid="wide-agenda-panel"
      aria-label="目次"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        background: PANEL_BG,
        color: '#fff',
        fontFamily: WIDE_FONT_FAMILY,
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '14px 12px 10px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <span style={{ fontSize: '14px', fontWeight: 800 }}>目次</span>
        {/* 見ている場面があるときだけ「2 / 5」を出す。最初の場面より前の「全5件」は一覧を見れば分かるので出さない */}
        {entries.some((entry) => entry.id === currentId) && (
          <span style={{ fontSize: '12px', color: SUB_COLOR, fontVariantNumeric: 'tabular-nums' }}>
            {formatAgendaPosition(entries, currentId)}
          </span>
        )}
        {headerAction && (
          <button
            type="button"
            onClick={headerAction.onClick}
            onMouseEnter={() => setIsHeaderActionHovered(true)}
            onMouseLeave={() => setIsHeaderActionHovered(false)}
            aria-label={headerAction.kind === 'collapse' ? '目次をたたむ' : '目次を閉じる'}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              marginLeft: 'auto',
              padding: 0,
              border: 'none',
              borderRadius: '6px',
              // マウスを乗せたら、目次の行と同じく黒く沈める
              background: isHeaderActionHovered ? HOVER_BG : 'rgba(255,255,255,0.08)',
              transition: 'background 0.15s',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            {headerAction.kind === 'collapse' ? <ChevronsLeft size={16} /> : <X size={16} />}
          </button>
        )}
      </div>

      {/* 行どうしの間は gap で空ける（行の押せる範囲＝padding は広げない） */}
      <ol
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          margin: 0,
          padding: '10px',
          listStyle: 'none',
        }}
      >
        {entries.map((entry, index) => {
          const isCurrent = entry.id === currentId
          const isHovered = !disabled && entry.id === hoveredId
          return (
            <li key={entry.id}>
              <button
                type="button"
                data-testid="wide-agenda-item"
                aria-current={isCurrent ? 'true' : undefined}
                disabled={disabled}
                onClick={() => onSelect(entry.id)}
                onMouseEnter={() => setHoveredId(entry.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px',
                  border: 'none',
                  borderRadius: '8px',
                  background: isCurrent
                    ? isHovered
                      ? CURRENT_HOVER_BG
                      : CURRENT_BG
                    : isHovered
                      ? HOVER_BG
                      : 'transparent',
                  transition: 'background 0.15s',
                  color: '#fff',
                  fontFamily: 'inherit',
                  fontSize: '14.5px',
                  fontWeight: isCurrent ? 800 : 600,
                  lineHeight: 1.4,
                  textAlign: 'left',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.5 : 1,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: 'flex',
                    flexShrink: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '22px',
                    height: '22px',
                    border: `1px solid ${isCurrent ? '#fff' : 'rgba(255,255,255,0.3)'}`,
                    borderRadius: '50%',
                    background: isCurrent ? '#fff' : 'transparent',
                    color: isCurrent ? PANEL_BG : '#fff',
                    fontSize: '11px',
                    fontWeight: 800,
                  }}
                >
                  {isCurrent ? <Play size={10} fill="currentColor" /> : index + 1}
                </span>
                <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{entry.label}</span>
                {/* 行き先の目印: 同じ動画の秒数／別の動画／外部リンク */}
                <span
                  aria-hidden="true"
                  style={{ flexShrink: 0, color: SUB_COLOR, fontSize: '11px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                >
                  {entry.action.type === 'seek' ? (
                    formatPlaybackTime(entry.action.time)
                  ) : entry.action.type === 'video_transition' ? (
                    <ArrowRight size={13} />
                  ) : (
                    <ArrowUpRight size={13} />
                  )}
                </span>
              </button>
            </li>
          )
        })}
      </ol>

        {cta && (
          <div style={{ padding: '12px' }}>
            <button
              type="button"
              onClick={() => window.open(cta.url, '_blank', 'noopener,noreferrer')}
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '11px',
                border: 'none',
                borderRadius: '8px',
                background: '#fff',
                color: '#172554',
                fontFamily: 'inherit',
                fontSize: '13px',
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              {cta.label}
              <ArrowUpRight size={14} />
            </button>
          </div>
        )}
      {backAction && (
        <button
          type="button"
          data-testid="wide-agenda-back"
          onClick={backAction.onClick}
          onMouseEnter={() => setIsBackHovered(true)}
          onMouseLeave={() => setIsBackHovered(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            padding: '12px 16px',
            border: 'none',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            background: isBackHovered ? HOVER_BG : 'transparent',
            transition: 'background 0.15s',
            color: '#fff',
            fontFamily: 'inherit',
            fontSize: '13px',
            fontWeight: 700,
            textAlign: 'left',
            cursor: 'pointer',
          }}
        >
          <ArrowLeft size={14} style={{ flexShrink: 0 }} />
          前の動画へ
        </button>
      )}
    </nav>
  )
}

export default WideAgendaPanel
